import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Loading,
  Select,
  StatusBadge,
  Textarea,
  Tooltip,
} from '../components/ui';
import { api, type Activity } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDraftApproval } from '../lib/draftApproval';
import { channelLabel, dateTime, humanize, relative } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

interface BounceBreaker {
  tripped: boolean;
  sends: number;
  bounces: number;
  reason: string | null;
}

/**
 * Shown while email sending is paused because too many recent sends bounced
 * (backlog #46). Scheduled emails wait and manual sends are refused until an admin
 * resumes; resuming starts the count afresh, so a list that is still bad trips it again.
 */
function BounceBreakerBanner() {
  const { user } = useAuth();
  const breaker = useApi<BounceBreaker>('/api/outreach/bounce-breaker');
  const resume = useAction(async () => {
    await api.post('/api/outreach/bounce-breaker/release');
    breaker.reload();
  });

  if (!breaker.data?.tripped) return null;
  return (
    <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-800 ring-1 ring-inset ring-red-200" role="alert">
      <p className="font-medium">Email sending is paused</p>
      <p className="mt-1">{breaker.data.reason}</p>
      <p className="mt-1">
        The addresses that bounced are on the suppression list and are not emailed again. Scheduled
        emails stay scheduled; LinkedIn is not affected.
      </p>
      {user?.role === 'admin' ? (
        <div className="mt-3">
          <Button loading={resume.pending} onClick={() => void resume.run()}>
            Resume sending
          </Button>
          {resume.error && <div className="mt-2"><ErrorNote message={resume.error} /></div>}
        </div>
      ) : (
        <p className="mt-2 text-xs">An admin has to resume sending.</p>
      )}
    </div>
  );
}

/**
 * Batch review: work top to bottom, edit inline, approve or skip. This is the
 * screen that replaces clicking through Lemlist one prospect at a time.
 */
export function Queue() {
  const [channel, setChannel] = useState('');
  const path = `/api/outreach/queue?limit=100${channel ? `&channel=${channel}` : ''}`;
  const queue = useApi<{ queue: Activity[] }>(path);

  const runSequences = useAction(async () => {
    await api.post('/api/outreach/sequences/run');
    queue.reload();
  });

  const items = queue.data?.queue ?? [];

  return (
    <>
      <PageHeader
        title="Review queue"
        description={`${items.length} drafts waiting for approval`}
        helpHref="queue"
        actions={
          <>
            <div className="w-44">
              <Select value={channel} onChange={(event) => setChannel(event.target.value)}>
                <option value="">All channels</option>
                <option value="email">Email</option>
                <option value="linkedin_dm">LinkedIn DM</option>
                <option value="linkedin_post_engage">LinkedIn post</option>
              </Select>
            </div>
            <Tooltip content="Checks every active sequence for steps that are due and drafts them, so they show up here for review.">
              <Button loading={runSequences.pending} onClick={() => void runSequences.run()}>
                Draft due follow-ups
              </Button>
            </Tooltip>
          </>
        }
      />

      <BounceBreakerBanner />
      {runSequences.error && <div className="mb-3"><ErrorNote message={runSequences.error} /></div>}
      {queue.loading && <Loading />}
      {queue.error && <ErrorNote message={queue.error} onRetry={queue.reload} />}

      {!queue.loading && items.length === 0 && (
        <EmptyState
          title="Queue is empty"
          description="Draft outreach from a prospect page, or run the cadence to generate anything that is due."
          action={
            <Button variant="primary" loading={runSequences.pending} onClick={() => void runSequences.run()}>
              Draft due follow-ups
            </Button>
          }
        />
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <QueueCard key={item.id} activity={item} onChanged={queue.reload} />
        ))}
      </div>
    </>
  );
}

function QueueCard({ activity, onChanged }: { activity: Activity; onChanged: () => void }) {
  const [subject, setSubject] = useState(activity.subject ?? '');
  const [body, setBody] = useState(activity.your_edits ?? activity.claude_draft);
  const [manual, setManual] = useState<{ payload: string; url: string | null } | null>(null);
  const [done, setDone] = useState(false);

  const dirty = body !== (activity.your_edits ?? activity.claude_draft) || subject !== (activity.subject ?? '');

  // Save-then-approve ordering and the admin gate live in one place, shared
  // with the prospect record page — see lib/draftApproval.ts.
  const { isAdmin, approved, willApprove, canSend, commitAndApprove, sendLabel, scheduleLabel } =
    useDraftApproval(activity, { subject, body, dirty });

  const send = useAction(async () => {
    await commitAndApprove();
    const result = await api.post<{ manualPayload?: { payload: string; url: string | null } }>(
      `/api/outreach/${activity.id}/send`,
    );
    if (result.manualPayload) setManual(result.manualPayload);
    else setDone(true);
    onChanged();
  });

  const skip = useAction(async () => {
    await api.post(`/api/outreach/${activity.id}/skip`);
    setDone(true);
    onChanged();
  });

  // Queues the send instead of doing it now: the backend picks the next slot
  // inside the campaign's send window, in the recipient's timezone, and the
  // scheduler sends it then.
  const schedule = useAction(async () => {
    await commitAndApprove();
    await api.post(`/api/outreach/${activity.id}/schedule`);
    onChanged();
  });

  /** Approve and leave in the queue, for someone else to send. Admin only. */
  const approve = useAction(async () => {
    await commitAndApprove();
    onChanged();
  });

  const unapprove = useAction(async () => {
    await api.post(`/api/outreach/${activity.id}/unapprove`);
    onChanged();
  });

  const save = useAction(async () => {
    await api.put(`/api/outreach/${activity.id}`, { subject, body });
    onChanged();
  });

  if (done && !manual) return null;

  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={`/prospects/${activity.prospect_id}`}
            className="font-semibold text-ink-900 hover:text-accent-700"
          >
            {activity.prospect_name}
          </Link>
          <span className="font-normal text-ink-500">{activity.company_name}</span>
          <Badge>{channelLabel(activity.channel)}</Badge>
          <Badge tone="neutral">{humanize(activity.message_type)}</Badge>
          <StatusBadge status={activity.status} />
          {activity.personalization_grounded === false && (
            <Badge tone="amber" title={activity.personalization_note}>
              Generic — no specific fact cited
            </Badge>
          )}
        </span>
      }
      action={
        <span className="text-xs text-ink-400">
          {activity.scheduled_for
            ? `scheduled ${dateTime(activity.scheduled_for)}`
            : `drafted ${relative(activity.created_at)}`}
        </span>
      }
    >
      <div className="space-y-2">
        {activity.channel === 'email' && (
          <Input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Subject"
            className="font-medium"
          />
        )}
        <Textarea rows={7} value={body} onChange={(event) => setBody(event.target.value)} />

        {activity.channel === 'email' && !activity.prospect_email && (
          <ErrorNote message="This prospect has no email address — add one before sending." />
        )}
        {activity.channel === 'email' && activity.prospect_email && activity.prospect_email_status === 'inferred' && (
          <ErrorNote
            message={`${activity.prospect_email} is an unconfirmed (guessed) address — sending is blocked until it is confirmed on the prospect record.`}
          />
        )}
        {(send.error || save.error || skip.error || schedule.error || approve.error || unapprove.error) && (
          <ErrorNote
            message={
              (send.error ??
                save.error ??
                skip.error ??
                schedule.error ??
                approve.error ??
                unapprove.error) as string
            }
          />
        )}

        {manual && (
          <div className="rounded-md bg-accent-50 p-3 text-sm ring-1 ring-inset ring-accent-200">
            <p className="font-medium text-accent-900">Copy this into LinkedIn</p>
            <p className="mt-1 whitespace-pre-wrap text-ink-800">{manual.payload}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => void navigator.clipboard.writeText(manual.payload)}>
                Copy message
              </Button>
              {manual.url && (
                <a
                  href={manual.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-ink-50"
                >
                  Open profile
                </a>
              )}
              <Button size="sm" variant="ghost" onClick={() => setDone(true)}>
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {approved && !dirty && (
          <p className="text-xs text-ink-500">
            Approved{activity.approved_by_name ? ` by ${activity.approved_by_name}` : ''}
            {activity.approved_at ? ` ${relative(activity.approved_at)}` : ''}.
          </p>
        )}
        {approved && dirty && (
          <p className="text-xs text-amber-700">
            Editing this message withdraws its approval
            {isAdmin ? ' — sending or scheduling re-approves it.' : ', so an admin will need to approve it again.'}
          </p>
        )}
        {!approved && !isAdmin && (
          <p className="text-xs text-ink-500">
            An admin has to approve this before it can be sent or scheduled.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {canSend && (
            <Button variant="primary" size="sm" loading={send.pending} onClick={() => void send.run()}>
              {sendLabel}
            </Button>
          )}
          {activity.channel === 'email' && activity.status !== 'scheduled' && canSend && (
            <Tooltip content="Queues the send for the campaign's next send window, in the recipient's own timezone, instead of sending immediately.">
              <Button size="sm" loading={schedule.pending} onClick={() => void schedule.run()}>
                {scheduleLabel}
              </Button>
            </Tooltip>
          )}
          {willApprove && (
            <Tooltip content="Marks it approved without sending — anyone can send or schedule it afterwards.">
              <Button size="sm" loading={approve.pending} onClick={() => void approve.run()}>
                Approve only
              </Button>
            </Tooltip>
          )}
          {isAdmin && approved && !dirty && (
            <Button size="sm" loading={unapprove.pending} onClick={() => void unapprove.run()}>
              Withdraw approval
            </Button>
          )}
          {dirty && (
            <Button size="sm" loading={save.pending} onClick={() => void save.run()}>
              Save edits
            </Button>
          )}
          <Button variant="ghost" size="sm" loading={skip.pending} onClick={() => void skip.run()}>
            Skip
          </Button>
        </div>
      </div>
    </Card>
  );
}
